// Read-only panels: the chips over the net, the density scale under it, the density
// legend and the whole-data statistics. GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import { t, tn } from "../../i18n/index.js";
import { $, setHTML } from "../../lib/dom.js";
import { fmt, pad2, pad3, fmtPlane, fmtName, int } from "../../lib/format.js";
import { ramp } from "../../lib/theme.js";
import { weighted } from "../../state/derived.js";
import { X, P, onRender } from "./ctx.js";
import { onDensity } from "./density.js";

export const methodName = () => (P().stereo.density.method === "schmidt" ? "Schmidt" : "Fisher");
export const projName = () => {
  const pr = P().stereo.projection;
  return `${pr.method === "equal-area" ? t("Equal area") : t("Equal angle")} · ${pr.hemisphere === "lower" ? t("lower hemisphere") : t("upper hemisphere")}`;
};

function renderStats() {
  const el = $("stats"), d = X.d, f = P().data.format;
  if (!d.stats || !d.gm) { setHTML(el, `<p class="muted">${t("At least 2 poles are needed")}</p>`); return; }
  const g = S.poleToPlane(d.gm), b = d.bm ? S.poleToPlane(d.bm) : null, bl = d.bm ? S.vecToLine(d.bm) : null;
  const rows: [string, string][] = [
    [t("Poles"), int(d.stats.n) + (d.stats.weightSum !== d.stats.n ? ` (${t("weight sum {w}", { w: fmt(d.stats.weightSum, 0) })})` : "")],
    [`${t("Global mean, gm")} (${fmtName(f)})`, fmtPlane(g.dip, g.dipDir, f)],
    [`${t("Global best fit, bm")} (${fmtName(f)})`, b ? fmtPlane(b.dip, b.dipDir, f) : "–"],
    [t("bm axis (trend/plunge)"), bl ? `${pad3(bl.trend)}/${pad2(bl.plunge)}` : "–"],
  ];
  setHTML(el, `<dl>${rows.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join("")}</dl>
    <p class="muted small">${t("gm is the vector sum of all poles — suited to a single, not too steep cluster. bm is the plane that best fits the trend of the poles — used for the axis of a girdle, e.g. a fold axis.")}</p>`);
}

/** Live figures above the net: what the screen is showing, without opening a tab. */
function renderChips() {
  const p = P(), c: string[] = [], n = p.data.planes.length;
  c.push(`<span class="chip"><span class="dotc"></span>${tn(n, "<b>{n}</b> pole", "<b>{n}</b> poles")}</span>`);
  if (p.sets.length) c.push(`<span class="chip">${tn(p.sets.length, "<b>{n}</b> set", "<b>{n}</b> sets")}</span>`);
  c.push(`<span class="chip">${projName()}</span>`);
  if (X.grid && p.stereo.display.contours) c.push(`<span class="chip">${methodName()} · ${t("max")} <b>${fmt(X.grid.max, 2)}%</b></span>`);
  if (weighted(p)) c.push(`<span class="chip warn">Terzaghi</span>`);
  // one element per chip, updated in place: only the chip whose text changed is touched
  const box = $("chips");
  while (box.children.length > c.length) box.lastElementChild!.remove();
  while (box.children.length < c.length) box.appendChild(document.createElement("span"));
  c.forEach((html, i) => { const el = box.children[i] as HTMLElement; const m = /^<span class="([^"]*)">(.*)<\/span>$/s.exec(html)!; if (el.className !== m[1]) el.className = m[1]; setHTML(el, m[2]); });
}

/** The contour ramp, written out under the net the way a map carries its scale. */
function renderScale() {
  const el = $("scalewrap"), p = P(), show = p.stereo.display.view === "net" && p.stereo.display.contours && !!X.grid && X.levels.length > 0;
  el.hidden = !show; if (!show) return;
  const cols = ramp(), edges = [...X.levels.slice(1), X.grid!.max];
  const rampHTML = p.stereo.display.contourStyle === "smooth" ? `<i style="background:linear-gradient(90deg,${cols.join(",")})"></i>` : cols.map((c) => `<i style="background:${c}"></i>`).join("");
  setHTML(el, `<div class="scalebar"><span class="cap">${t("Density (% per 1% area)")}</span>
    <div class="ramps"><div class="ramp">${rampHTML}</div>
      <div class="tk">${edges.map((v) => `<span>${fmt(v, 1)}</span>`).join("")}</div></div>
    <span class="mx">${t("max")} <b>${fmt(X.grid!.max, 2)}%</b></span></div>`);
}

/** Band edges as in DipField: one item per contour level, [level, next level or max]. */
function renderLegend() {
  const el = $("legend"), p = P();
  if (!p.stereo.display.contours || !X.grid || !X.levels.length) { setHTML(el, ""); return; }
  const cols = ramp(), edges = [0, ...X.levels, X.grid.max];
  setHTML(el, `<p class="legend-title">${t("Pole density (% per 1% area)")}</p><ol class="legend-list">` +
    edges.slice(1, -1).map((lo, i) => `<li><span class="sw" style="background:${cols[Math.min(cols.length - 1, i)]}"></span>${fmt(lo, 2)} – ${fmt(edges[i + 2], 2)}</li>`).join("") +
    `</ol><p class="muted small">${t("max {m}% · {method} · counting circle {c}%", { m: fmt(X.grid.max, 2), method: methodName(), c: fmt(p.stereo.density.countCircle * 100, 1) })}</p>`);
}

export function mountPanels() {
  onRender(() => { renderStats(); renderChips(); renderScale(); renderLegend(); });
  onDensity(() => { renderLegend(); renderScale(); renderChips(); });
}
